import { Button } from '@/view/components/ui/button'
import { Archive, ChevronRight } from 'lucide-react'
import masks from '@/app/utils/masks'
import { PartnerType } from '@/app/utils/partners.enum'
import { getImageByType } from '@/app/utils/getImageByType'
import { Customer } from '@/app/entities/Customer'

type CardVariant = 'withBalance' | 'unauthorized' | 'archived'

export type CustomerCardData = {
  _id: string
  name: string
  phone: string
  cpf?: string
  zipCode?: string
  type: PartnerType
  available?: {
    amount?: number
    isAutorized?: boolean
    isContacted?: boolean
    updatedAt?: string
  }
}

export function extractCustomerCards(customer: Customer): CustomerCardData[] {
  const cards: CustomerCardData[] = []

  if (customer.available) {
    cards.push({
      _id: customer._id,
      name: customer.name,
      phone: customer.phone,
      cpf: customer.cpf,
      zipCode: customer.address?.zipCode,
      type: PartnerType.FGTS,
      available: {
        amount: customer.available.amount,
        isAutorized: customer.available.isAutorized,
        isContacted: customer.available.isContacted,
        updatedAt: customer.available.updatedAt,
      },
    })
  }

  if (customer.available?.crefaz) {
    const crefaz = customer.available.crefaz
    const crefazAmount =
      typeof crefaz.amount === 'string' ? Number(crefaz.amount) : crefaz.amount
    cards.push({
      _id: customer._id,
      name: customer.name,
      phone: customer.phone,
      cpf: customer.cpf,
      zipCode: customer.address?.zipCode,
      type: PartnerType.CREFAZ,
      available: {
        amount: crefazAmount,
        isAutorized: customer.available.isAutorized,
        isContacted: crefaz.isContacted,
        updatedAt: crefaz.updatedAt,
      },
    })
  }

  return cards
}

type Props = {
  customer: CustomerCardData
  contacted: boolean
  onClick: () => void
  onContactClick?: () => void
  onArchiveClick?: () => void
  onUnarchiveClick?: () => void
  onProposeClick?: () => void
  variant: CardVariant
}

export const CustomerCard = ({
  customer,
  contacted,
  onClick,
  onContactClick,
  onArchiveClick,
  onUnarchiveClick,
  onProposeClick,
  variant,
}: Props) => {
  return (
    <div
      className={`w-[330px] cursor-pointer rounded-lg border-[1px] p-5 ${
        contacted ? 'border-gray01' : 'border-primaryHex'
      }`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-[16px] font-bold text-black">{customer.name}</p>
          <p className="text-[14px] text-black04">
            Telefone:{' '}
            <span className="text-black">{masks.phone(customer.phone)}</span>
          </p>
          <img
            src={getImageByType(customer.type)}
            className="mt-1 h-8 w-8 rounded-lg object-contain"
            alt={customer.type}
          />
        </div>
        <Button
          className="flex h-12 w-12 rounded-full bg-green-600 p-0 shadow-lg hover:bg-green-500"
          onClick={(event) => {
            event.stopPropagation()
            onContactClick?.()
          }}
        >
          <img src="/whatsapp.svg" alt="icone whatsapp" />
        </Button>
      </div>

      {customer.available?.amount && customer.available?.isAutorized ? (
        <div className="mt-4 flex items-center justify-between rounded-md border-[1px] border-green-600 bg-white02 px-2 py-1">
          <p className="text-[20px] font-bold text-black">
            {masks.currency(customer.available.amount)}
          </p>
          <p className="text-[10px] text-black04">
            Saldo em {masks.date(customer.available.updatedAt)}
          </p>
        </div>
      ) : (
        <div className="mt-4 flex items-center justify-center rounded-md border-[1px] border-rose-300 bg-rose-100 px-2 py-1">
          <p className="text-[18px] font-bold text-rose-400">Não autorizado</p>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        {variant === 'withBalance' && (
          <>
            <Button
              className="flex h-11 w-12 rounded-lg border-[1px] border-gray-200 bg-white p-0"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation()
                onArchiveClick?.()
              }}
            >
              <Archive />
            </Button>
            <Button
              className="flex w-full items-center justify-between"
              onClick={(e) => {
                e.stopPropagation()
                onProposeClick?.()
              }}
            >
              Realizar proposta
              <ChevronRight size={20} />
            </Button>
          </>
        )}

        {variant === 'unauthorized' && (
          <>
            <Button
              className="flex h-11 w-12 rounded-lg border-[1px] border-gray-200 bg-white p-0"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation()
                onArchiveClick?.()
              }}
            >
              <Archive />
            </Button>
            <Button
              className="flex w-full items-center justify-between bg-green-600 px-4 hover:bg-green-500"
              onClick={(e) => {
                e.stopPropagation()
                onContactClick?.()
              }}
            >
              Contatar
              <ChevronRight size={20} />
            </Button>
          </>
        )}

        {variant === 'archived' && (
          <Button
            className="w-full border-[1px] border-gray-200 bg-white text-black"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              onUnarchiveClick?.()
            }}
          >
            Desarquivar
            <Archive size={20} style={{ marginLeft: 6 }} />
          </Button>
        )}
      </div>
    </div>
  )
}
